import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Result, Button, Typography, Card } from 'antd';
import { ReloadOutlined, BugOutlined } from '@ant-design/icons';
import { colors, spacing, shadows } from '../../styles/theme';

const { Paragraph, Text } = Typography;

interface Props {
  children: ReactNode;
  /** 커스텀 에러 메시지 */
  fallbackMessage?: string;
  /** 에러 발생 시 호출될 콜백 */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** 리셋 버튼 표시 여부 */
  showResetButton?: boolean;
  /** 개발 모드에서 에러 상세 정보 표시 여부 */
  showErrorDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * 통합 에러 바운더리 컴포넌트
 * 
 * React 에러를 캐치하고 사용자 친화적인 에러 페이지를 표시합니다.
 * 개발/프로덕션 환경에 따라 다른 수준의 에러 정보를 제공합니다.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true,
      error 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // 외부 에러 리포팅 서비스에 에러 전송
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined 
    });
  };

  render() {
    const { 
      children, 
      fallbackMessage = '알 수 없는 오류가 발생했습니다.',
      showResetButton = true,
      showErrorDetails = process.env.NODE_ENV === 'development'
    } = this.props;

    if (this.state.hasError) {
      const { error, errorInfo } = this.state;

      return (
        <div style={{
          minHeight: '400px',
          padding: spacing[6],
          backgroundColor: colors.background.secondary,
        }}>
          <Card
            style={{
              maxWidth: '800px',
              margin: '0 auto',
              boxShadow: shadows.card,
            }}
          >
            <Result
              status="error"
              icon={<BugOutlined style={{ color: colors.error[500] }} />}
              title="오류가 발생했습니다"
              subTitle={fallbackMessage}
              extra={[
                showResetButton && (
                  <Button 
                    type="primary" 
                    key="reset"
                    icon={<ReloadOutlined />}
                    onClick={this.handleReset}
                  >
                    다시 시도
                  </Button>
                ),
                <Button 
                  key="refresh"
                  onClick={() => window.location.reload()}
                >
                  페이지 새로고침
                </Button>
              ].filter(Boolean)}
            >
              <div style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}>
                <Paragraph>
                  <Text type="secondary">
                    불편을 드려 죄송합니다. 문제가 지속되면 관리자에게 문의해주세요.
                  </Text>
                </Paragraph>

                {showErrorDetails && error && (
                  <Card 
                    title="에러 상세 정보 (개발용)"
                    size="small"
                    style={{ 
                      marginTop: spacing[4],
                      backgroundColor: colors.background.tertiary,
                    }}
                  >
                    <Paragraph>
                      <Text code strong>에러 메시지:</Text>
                      <br />
                      <Text code style={{ color: colors.error[600] }}>
                        {error.message}
                      </Text>
                    </Paragraph>

                    {error.stack && (
                      <Paragraph>
                        <Text code strong>스택 트레이스:</Text>
                        <br />
                        <Text code style={{ 
                          fontSize: '12px',
                          color: colors.text.secondary,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}>
                          {error.stack}
                        </Text>
                      </Paragraph>
                    )}

                    {errorInfo?.componentStack && (
                      <Paragraph>
                        <Text code strong>컴포넌트 스택:</Text>
                        <br />
                        <Text code style={{ 
                          fontSize: '12px',
                          color: colors.text.secondary,
                          whiteSpace: 'pre-wrap',
                        }}>
                          {errorInfo.componentStack}
                        </Text>
                      </Paragraph>
                    )}
                  </Card>
                )}
              </div>
            </Result>
          </Card>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;